import { isApiError, isSchemaValidationError } from '@brightlocal/api-client';

import type { ReportCreateStep, ReportCreateTool } from '../consts';

interface ReportCreateErrorParams {
  /** Tool whose report was being created */
  tool: ReportCreateTool;
  /** Step that failed */
  step: ReportCreateStep;
  /** The original failure: an api-client `ApiError` or an `Error` */
  cause: unknown;
  /**
   * Stable key for a failure the app detected itself (e.g. `businessCategoryMissing`).
   * API failures take their key from the response body instead.
   */
  reason?: string;
  /** Debugging context attached to the Sentry report (location id, country, …) */
  context?: Record<string, unknown>;
}

/**
 * Failure of one report-creation step. Carries the tool, the step and the mutation variables
 * that the central error reporting needs but cannot see, because it only receives the thrown error.
 */
export class ReportCreateError extends Error {
  readonly tool: ReportCreateTool;
  readonly step: ReportCreateStep;
  readonly context: Record<string, unknown>;
  /** The original failure; set here because the app's TS lib has no `ErrorOptions`. */
  readonly cause: unknown;
  readonly reason?: string;

  constructor({ tool, step, reason, context = {}, cause }: ReportCreateErrorParams) {
    super(`Error creating ${tool} report: ${step} failed`);
    this.name = 'ReportCreateError';
    this.tool = tool;
    this.step = step;
    this.reason = reason;
    this.context = context;
    this.cause = cause;
  }
}

/** Narrows an unknown error to a `ReportCreateError`. */
export const isReportCreateError = (error: unknown): error is ReportCreateError => error instanceof ReportCreateError;

interface RunReportStepParams<T> {
  /** Tool whose report is being created */
  tool: ReportCreateTool;
  /** Step being run */
  step: ReportCreateStep;
  /** The API call of this step */
  request: () => Promise<T>;
  /** Debugging context attached to a failure */
  context?: Record<string, unknown>;
}

/**
 * Runs one report-creation step and wraps its failure in a `ReportCreateError`.
 * Response-schema mismatches are rethrown as they are so they keep their dedicated Sentry channel.
 */
export const runReportStep = async <T>({ tool, step, context, request }: RunReportStepParams<T>) => {
  try {
    return await request();
  } catch (error) {
    if (isApiError(error) && isSchemaValidationError(error)) {
      throw error;
    }

    throw new ReportCreateError({ tool, step, context, cause: error });
  }
};
